import type { OnInit } from '@angular/core';
import type { VideoText } from '@web/widgets/video/model/video.model';
import type { ReplacedFile } from '@components/sp-file-manager/src/types';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { TranslateService } from '@ngx-translate/core';

import { ESize, EVideoTypes, WidgetsDataTypes } from '@common/enums';
import { extendWebFileDataPromise, getYoutubeVideoCode, wrapYoutubeCodeIntoLink } from '@common/helpers';
import { CommonHttpService } from '@common/services/REST/common.http.service';
import { ESizeOptions } from '@common/constants/background-size-options.const';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { FmSourceType, DirectoryItemCategory, type SelectedFileInterface } from '@sendpulse/file-manager';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import { formatFileUrl } from '@components/sp-file-manager/src/helpers';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { environment } from '@web-env/environment';
import { toSignal } from '@angular/core/rxjs-interop';

import {
    validateVideoUrl,
    type ExtendedPositionLegacy,
    type LegacyVideoDefault,
    type VideoEntity,
    type VideoUrlError,
} from '@sites/entity-core';
import type { VideoDefaultForm, VideoForm, VideoZeroForm } from '@sites/entity-signal-form';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { WebDeviceToggleComponent } from '@web/shared/components/web-device-toggle/web-device-toggle.component';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings/web-widget-extended-hide-settings.component';

import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

/**
 * Editor control panel for the `video` widget — fully signal-based.
 *
 * Inputs (signal `input()`s, populated by `ControlResolverComponent.setInput`):
 *  - `widget` — the initial widget snapshot, used for the static envelope
 *    (id, columnId for DOM lookup; the stable display id comes from `form.widgetId()`).
 *  - `isSpecific`, `isEduSite` — booleans inherited from the legacy contract.
 *
 * The form is created internally in `ngOnInit` via `WebLegacyWidgetSignalFormService.getOrCreateScoped`,
 * which ref-counts by widget id — the view component for the same widget shares the
 * same form instance, with the form destroyed only once both consumers release.
 */
@Component({
    selector: 'web-video-control',
    templateUrl: './video-control.component.html',
    styleUrls: ['./video-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        TooltipModule,
        SpFileManagerComponentModule,
        FormField,
        SpExtendedPositionSignalSettingsComponent,
        WebColorPickerComponent,
        WebDeviceToggleComponent,
        WebWidgetExtendedHideSettingsComponent,
    ],
})
export class VideoControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<VideoEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly commonHttpService: CommonHttpService = inject(CommonHttpService);
    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    /** Device-mode signals used by the extended-position editor for the zero variant. */
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    public form!: VideoForm;

    public readonly FmSourceType = FmSourceType;
    public readonly DirectoryItemCategory = DirectoryItemCategory;
    public readonly EVideoTypes = EVideoTypes;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly ESize = ESize;

    public buttonDisabled = signal(false);
    public columnWidth = 0;

    /**
     * Typing buffer for the URL `<input>`. The canonical value lives in the
     * widget signal form; this local signal-form field holds in-progress text
     * (with its own `touched` state) until the user blurs and `setVideoUrl()`
     * normalises + commits to `this.form`.
     */
    private readonly urlBufferModel = signal<string>('');
    public readonly urlBuffer = form(this.urlBufferModel);

    public readonly videoTypeOptions = [
        { label: 'Youtube', type: EVideoTypes.youtube },
        { label: 'Vimeo', type: EVideoTypes.vimeo },
        { label: this.translateService.instant('file_label'), type: EVideoTypes.fileManager },
    ];

    public readonly previewSizeOptions = [
        { label: 'background_settings_size_stretch', value: ESizeOptions.cover },
        { label: 'background_settings_size_fit', value: ESizeOptions.contain },
    ];

    public readonly videoTexts: Partial<Record<EVideoTypes, VideoText>> = {
        [EVideoTypes.youtube]: { label: 'control_youtube_link', placeholder: 'https://youtube.com' },
        [EVideoTypes.vimeo]: { label: 'control_vimeo_link', placeholder: 'https://vimeo.com' },
        [EVideoTypes.fileManager]: { label: 'file_label', placeholder: '' },
    };

    public ngOnInit(): void {
        const form = this.signalFormService.getOrCreateScoped<VideoForm>(this.widget().id, this.destroyRef);
        // No factory/entity for this widget — bail before dereferencing a null form.
        if (!form) {
            return;
        }
        this.form = form;
        this.urlBuffer().reset(this.form.videoUrl().value() ?? '');
        this.recomputeColumnWidth();
    }

    /**
     * True when the widget lives inside a zero block. Drives template
     * branching: the layout settings panel swaps between the default
     * (`horizontalPosition` + `maxWidth`) and zero (extended-position +
     * hide + animation) UIs.
     */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    /**
     * The form narrowed to the default variant. Use this getter in code
     * paths gated by `!isZero` — the cast is safe because the dispatcher
     * (`createVideoForm`) picks the matching factory based on the entity's
     * `type` field at construction time.
     */
    public get defaultForm(): VideoDefaultForm {
        return this.form as VideoDefaultForm;
    }

    /**
     * The form narrowed to the zero variant. Use this getter in code paths
     * gated by `isZero` — see {@link defaultForm} for the casting rationale.
     */
    public get zeroForm(): VideoZeroForm {
        return this.form as VideoZeroForm;
    }

    /**
     * Current URL validation error for the typed buffer + the form's videoType.
     * Delegates to the package's {@link validateVideoUrl} helper — the same one
     * the zod schema's `.superRefine` calls — with `strict: true` so the editor
     * also flags the `required` case (the schema is lenient about emptiness).
     */
    public urlError(): VideoUrlError | null {
        const type = this.form.videoType().value();
        if (!type) return null;
        return validateVideoUrl(type, this.urlBuffer().value(), { strict: true });
    }

    // --- Source ---

    public setVideoType(event: Event): void {
        const target = event.target as HTMLInputElement;
        const value = target.value as LegacyVideoDefault['data']['videoType'];
        const form = this.form;

        form.transact(() => {
            form.setVideoType(value);
            form.setVideoUrl('');
            form.setPreviewUrl('');
            if (value !== EVideoTypes.fileManager && form.videoFile().value()) {
                form.setVideoFile(null);
            }
        });

        this.urlBuffer().reset('');
    }

    public setVideoUrl(): void {
        if (this.urlError()) return;

        const inputUrl = this.urlBuffer().value();
        const form = this.form;
        const currentType = form.videoType().value();

        if (currentType === EVideoTypes.youtube) {
            const youtubeUrl = this.getYouTubeUrl(inputUrl);
            const preview = this.getYoutubePreviewUrl(inputUrl);
            const shorts = this.isShortsVideo(inputUrl);

            form.transact(() => {
                form.setVideoUrl(youtubeUrl);
                form.setPreviewUrl(preview);
                form.setVideoFile(null);
                if (shorts !== form.isShorts().value()) {
                    form.setIsShorts(shorts);
                }
            });
            return;
        }

        // Vimeo: thumbnail comes from the host API; both success and failure paths
        // still commit the URL so the widget reflects what the user entered.
        this.commonHttpService.getVimeoPreview(`?url=${encodeURIComponent(inputUrl)}`).subscribe(
            (res) => {
                form.transact(() => {
                    form.setVideoUrl(inputUrl);
                    form.setPreviewUrl(res.thumbnail_url);
                    form.setVideoFile(null);
                });
            },
            () => {
                form.transact(() => {
                    form.setVideoUrl(inputUrl);
                    form.setPreviewUrl(null);
                    form.setVideoFile(null);
                });
            }
        );
    }

    public removeVideoFile(): void {
        this.form.setVideoFile(null);
    }

    /**
     * `replaceFileEvent` emits a `{ file, index }` wrapper, unlike
     * `setFileEvent` which emits the file directly — unwrap to `file` so the
     * form stores the file itself, not the wrapper (which would corrupt the
     * persisted video source).
     */
    public replaceVideoFile({ file }: ReplacedFile): void {
        this.form.setVideoFile(file);
    }

    // --- Custom preview ---

    public async setCustomPreviewFile(file: SelectedFileInterface): Promise<void> {
        this.buttonDisabled.set(true);

        const extended: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        const previewUrl = formatFileUrl(extended, environment.fileManagerCdnUrl);
        const form = this.form;

        form.transact(() => {
            form.setCustomPreviewFile(extended);
            form.setCustomPreviewUrl(previewUrl);
        });

        this.buttonDisabled.set(false);

        if (form.videoType().value() !== EVideoTypes.fileManager) {
            this.setVideoUrl();
        }
    }

    public async replaceCustomPreviewFile({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled.set(true);

        const extended: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        const previewUrl = formatFileUrl(extended, environment.fileManagerCdnUrl);
        const form = this.form;

        form.transact(() => {
            form.setCustomPreviewFile(extended);
            form.setCustomPreviewUrl(previewUrl);
        });

        this.buttonDisabled.set(false);

        if (form.videoType().value() !== EVideoTypes.fileManager) {
            this.setVideoUrl();
        }
    }

    public openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({
            widgetId: this.widget().id,
            dataType: WidgetsDataTypes.customPreviewFile,
        });
    }

    public removeCustomPreviewUrl(): void {
        const form = this.form;
        form.transact(() => {
            form.setCustomPreviewFile(null);
            form.setCustomPreviewUrl('');
        });

        if (form.videoType().value() !== EVideoTypes.fileManager) {
            this.setVideoUrl();
        }
    }

    public setCustomPreviewSize(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as LegacyVideoDefault['data']['customPreviewSize'];
        this.form.setCustomPreviewSize(value);
    }

    // --- Play button ---

    public setPlayButtonSize(value: string): void {
        this.form.setPlayButtonSize(value as LegacyVideoDefault['data']['playButton']['size']);
    }

    public setPlayButtonBackgroundColor({ value }: ColorPickerEventPayload): void {
        this.form.setPlayButtonBackgroundColor(value);
    }

    public setPlayButtonIconColor({ value }: ColorPickerEventPayload): void {
        this.form.setPlayButtonIconColor(value);
    }

    public setPlayButtonHoverBackgroundColor({ value }: ColorPickerEventPayload): void {
        this.form.setPlayButtonHoverBackgroundColor(value);
    }

    public setPlayButtonHoverIconColor({ value }: ColorPickerEventPayload): void {
        this.form.setPlayButtonHoverIconColor(value);
    }

    // --- Layout (default variant) ---

    public setHorizontalPosition(value: string): void {
        this.defaultForm.setHorizontalPosition(value as LegacyVideoDefault['data']['horizontalPosition']);
    }

    public refreshVideoWidth(): void {
        this.defaultForm.setMaxWidth(this.columnWidth);
    }

    public setMaxWidth(value: number): void {
        this.defaultForm.setMaxWidth(value);
    }

    /**
     * Effective max width for the width `<input>` and the refresh-button
     * visibility. A fresh video stores `maxWidth: null` (renders at full column
     * width), and stored values may exceed a since-narrowed column — both are
     * resolved to the column width here for *display only*. Opening the panel
     * must not mutate the persisted entity, so this is read-only: the model is
     * only written on an explicit user edit (`setMaxWidth`/`refreshVideoWidth`).
     */
    public displayMaxWidth(): number {
        const stored = this.defaultForm.maxWidth().value();
        if (!stored) {
            return this.columnWidth;
        }
        return this.columnWidth && this.columnWidth < stored ? this.columnWidth : stored;
    }

    // --- Layout (zero variant) ---

    public setHideOnDesktop(value: boolean): void {
        this.zeroForm.setHideOnDesktop(value);
    }

    public setHideOnTablet(value: boolean): void {
        this.zeroForm.setHideOnTablet(value);
    }

    public setHideOnMobile(value: boolean): void {
        this.zeroForm.setHideOnMobile(value);
    }

    /**
     * Current position snapshot for the extended-position settings component.
     * `computed()` so it tracks the signal-form's position value — when drag
     * handles on the zero canvas update the form, the parent binding here
     * re-evaluates and the child component receives the new value. Plain
     * method calls would only re-fire on unrelated CD ticks.
     *
     * Returns `null` until the form is bootstrapped by `ngOnInit`; the child
     * component's input is nullable and the effect inside no-ops on null.
     */
    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        const f = this.form as VideoZeroForm | undefined;
        if (!f) return null;
        return f.position().value() as ExtendedPositionLegacy;
    });

    /**
     * Re-dispatches the position object emitted by the settings component
     * back into the signal form. The form's `onUpdate` callback in
     * `WebLegacyWidgetSignalFormService` records the change in history and updates the
     * store, just like every other write goes.
     */
    public onZeroPositionChange(value: ExtendedPositionLegacy): void {
        this.zeroForm.setPosition(value);
    }

    // --- Private helpers ---

    private recomputeColumnWidth(): void {
        const widget = this.widget();
        this.columnWidth = 0;
        if (!this.isSpecific()) {
            this.columnWidth = (
                document.querySelector(`[id='${widget.columnId}'] .video-widget-width`) as HTMLDivElement
            )?.offsetWidth;
        } else {
            this.columnWidth = document.getElementById(widget.columnId)?.offsetWidth;
        }
    }

    private getYoutubePreviewUrl(url: string): string {
        const code = getYoutubeVideoCode(url);
        return code.includes('http://') || code.includes('https://') ? null : wrapYoutubeCodeIntoLink(code);
    }

    private getYouTubeUrl(url: string): string {
        url = url.includes('shorts') ? url.replace('shorts', 'embed') : url;
        url = url.includes('live/') ? url.replace('live/', 'watch?v=') : url;
        return url;
    }

    private isShortsVideo(url: string): boolean {
        return url.includes('shorts');
    }
}
